import { Reveal, SectionHeading } from "@/components/site/Reveal";
import { IMAGES, EXPERIENCE } from "@/data/content";
import { motion } from "framer-motion";
import { Fish, Sprout, Cpu, Leaf, LinkIcon, Check } from "lucide-react";

const PILLARS = [
  { icon: Fish, label: "Practical aquaculture experience" },
  { icon: Sprout, label: "Scientific farm management" },
  { icon: Cpu, label: "Modern technology" },
  { icon: Leaf, label: "Sustainable production" },
  { icon: LinkIcon, label: "Market linkage" },
];

export default function About() {
  return (
    <section id="about" className="relative py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          <Reveal>
            <SectionHeading number="02" eyebrow="About AquaFionix" title="An integrated approach to modern aquaculture." />
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
              AquaFionix Integrated Aquaculture Pvt. Ltd. is an emerging aquaculture company focused on
              developing efficient, sustainable and commercially viable aquaculture operations. Our approach
              combines practical farming experience, scientific management, modern aquaculture systems and
              market-oriented production.
            </p>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">
              Our farming roadmap is centred on RAS-assisted, high-tech shrimp and crab farming
              with a zero-waste approach, complemented by makhana pond cultivation. Our objective
              is to create an integrated value chain from production to processing and market
              linkage.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {PILLARS.map((p, i) => (
                <motion.div
                  key={p.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex items-center gap-3 rounded-xl glass px-4 py-3"
                >
                  <span className="grid place-items-center h-9 w-9 rounded-lg bg-cyan-500/10 text-cyan-300 border border-cyan-400/20">
                    <p.icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm text-slate-200">{p.label}</span>
                </motion.div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-cyan-500/10 via-transparent to-emerald-500/10 blur-2xl" />
              <div className="relative overflow-hidden rounded-[1.75rem] border border-white/10">
                <img src={IMAGES.aboutOps} alt="Aquaculture farm operations" className="h-[440px] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030D18] via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-4 sm:left-6 glass rounded-2xl px-5 py-4 max-w-[240px]">
                <p className="font-mono-tech text-xs text-cyan-300">SEED → HARVEST → MARKET</p>
                <p className="mt-1 text-sm text-slate-200">End-to-end aquaculture, connected to real buyers.</p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Our Experience */}
        <div id="experience" className="mt-24 lg:mt-32">
          <Reveal>
            <SectionHeading number="03" eyebrow="Our Experience" title="Hands-on across the aquaculture value chain."
              subtitle="Practical experience built through real farm operations — from stocking and feeding to harvesting and market linkage." />
          </Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {EXPERIENCE.map((e, i) => (
              <motion.div
                key={e}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                className="group rounded-2xl border border-white/10 bg-[#071728]/70 p-5 hover:border-cyan-400/40 hover:-translate-y-1 transition-all duration-300"
              >
                <span className="font-mono-tech text-xs text-cyan-400/70">{String(i + 1).padStart(2, "0")}</span>
                <div className="mt-3 flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-400 shrink-0" />
                  <span className="text-sm sm:text-base font-medium text-slate-100">{e}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
